import React, { useState, useEffect } from 'react'
import { message, Pagination, Modal, Select } from 'antd'
import {
    getSubscriptionPlans,
    createSubscriptionPlan,
    updateSubscriptionPlan,
    deleteSubscriptionPlan,
    getSubscriptions,
    getUsers
} from '../../../Web Apis/webApis'
import Load from '../../Load/Load'
import { AiFillDelete, AiFillEdit, AiOutlineEye } from "react-icons/ai";
import { FaSearch, FaPlus } from 'react-icons/fa';
import { BsCreditCard } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { confirm } = Modal;

const AdminSubscriptions = () => {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('plans') // 'plans' or 'subscriptions'
    const [plans, setPlans] = useState([])
    const [subscriptions, setSubscriptions] = useState([])
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [showEditPlan, setShowEditPlan] = useState(false)
    const [showViewSubscription, setShowViewSubscription] = useState(false)
    const [selectedPlan, setSelectedPlan] = useState({})
    const [selectedSubscription, setSelectedSubscription] = useState({})
    const [action, setAction] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage] = useState(10)
    const [searchQuery, setSearchQuery] = useState("")
    const [statusFilter, setStatusFilter] = useState("")

    // Form states for plan
    const [planFormData, setPlanFormData] = useState({
        code: '',
        displayName: '',
        billingPeriod: 'month',
        durationUnit: 'month',
        durationCount: 1,
        currency: 'INR',
        price: 0,
        originalPrice: null,
        discountPercent: null,
        pricePerMonth: null,
        offerLabel: '',
        badge: { type: 'none', text: '' },
        features: [''],
        ctaText: 'Subscribe Now',
        canCancelAnytime: true,
        isActive: true,
        sortOrder: 0
    })

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    // Calculate the current items for the page
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage

    const filteredPlans = plans.filter((plan) => {
        const query = searchQuery.toLowerCase()
        const matchesSearch = (
            plan.displayName?.toLowerCase().includes(query) ||
            plan.code?.toLowerCase().includes(query) ||
            plan.description?.toLowerCase().includes(query)
        )
        const matchesStatus = !statusFilter || (statusFilter === 'active' ? plan.isActive : !plan.isActive)

        return matchesSearch && matchesStatus
    })

    const filteredSubscriptions = subscriptions.filter((subscription) => {
        const query = searchQuery.toLowerCase()
        const matchesSearch = (
            getUserName(subscription.user)?.toLowerCase().includes(query) ||
            subscription.plan?.displayName?.toLowerCase().includes(query) ||
            subscription.status?.toLowerCase().includes(query)
        )
        const matchesStatus = !statusFilter || subscription.status === statusFilter

        return matchesSearch && matchesStatus
    })

    const currentItems = activeTab === 'plans'
        ? filteredPlans.slice(indexOfFirstItem, indexOfLastItem)
        : filteredSubscriptions.slice(indexOfFirstItem, indexOfLastItem)

    const fetchPlans = async () => {
        try {
            setLoading(true)
            const response = await getSubscriptionPlans()
            if (response?.data?.success) {
                setPlans(response?.data?.data || [])
            }
        } catch (error) {
            console.log("Error fetching plans", error.message)
            message.error("Error fetching subscription plans")
        } finally {
            setLoading(false)
        }
    }

    const fetchSubscriptions = async () => {
        try {
            setLoading(true)
            const response = await getSubscriptions()
            if (response?.data?.success) {
                setSubscriptions(response?.data?.data || [])
            }
        } catch (error) {
            console.log("Error fetching subscriptions", error.message)
            message.error("Error fetching subscriptions")
        } finally {
            setLoading(false)
        }
    }

    const fetchUsers = async () => {
        try {
            const response = await getUsers()
            if (response?.data?.success) {
                setUsers(response?.data?.data || [])
            }
        } catch (error) {
            console.log("Error fetching users", error.message)
        }
    }

    useEffect(() => {
        if (activeTab === 'plans') {
            fetchPlans()
        } else {
            fetchSubscriptions()
            fetchUsers()
        }
    }, [activeTab, action])

    const getUserName = (userId) => {
        const user = users.find(u => u._id === userId)
        return user ? user.name : 'Unknown User'
    }

    const handleDeletePlan = async (plan) => {
        confirm({
            title: 'Are you sure you want to delete this subscription plan?',
            content: `Plan: ${plan.displayName}`,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    setLoading(true)
                    const response = await deleteSubscriptionPlan(plan._id)
                    if (response?.data?.success) {
                        message.success(response?.data?.message || "Plan deleted successfully")
                        setAction(!action)
                    }
                } catch (error) {
                    console.log("Error deleting plan", error.message)
                    message.error(error?.response?.data?.message || "Error deleting plan")
                } finally {
                    setLoading(false)
                }
            }
        })
    }

    const handleTogglePlanActive = async (plan, checked) => {
        try {
            setLoading(true)
            const response = await updateSubscriptionPlan(plan._id, { isActive: checked })
            if (response?.data?.success) {
                message.success(`Plan ${checked ? 'activated' : 'deactivated'} successfully`)
                setAction(!action)
            }
        } catch (error) {
            console.log("Error updating plan status", error.message)
            message.error(error?.response?.data?.message || "Error updating plan status")
        } finally {
            setLoading(false)
        }
    }

    const handleEditPlan = (plan) => {
        setSelectedPlan(plan)
        setPlanFormData({
            code: plan.code || '',
            displayName: plan.displayName || '',
            billingPeriod: plan.billingPeriod || 'month',
            durationUnit: plan.durationUnit || 'month',
            durationCount: plan.durationCount || 1,
            currency: plan.currency || 'INR',
            price: plan.price || 0,
            originalPrice: plan.originalPrice || null,
            discountPercent: plan.discountPercent || null,
            pricePerMonth: plan.pricePerMonth || null,
            offerLabel: plan.offerLabel || '',
            badge: plan.badge || { type: 'none', text: '' },
            features: plan.features || [''],
            ctaText: plan.ctaText || 'Subscribe Now',
            canCancelAnytime: plan.canCancelAnytime !== false,
            isActive: plan.isActive !== false,
            sortOrder: plan.sortOrder || 0
        })
        setShowEditPlan(true)
    }


    const handleViewSubscription = (subscription) => {
        setSelectedSubscription(subscription)
        setShowViewSubscription(true)
    }

    const handlePlanFormSubmit = async (isEdit = false) => {
        try {
            setLoading(true)
            let response

            if (isEdit) {
                response = await updateSubscriptionPlan(selectedPlan._id, planFormData)
            } else {
                response = await createSubscriptionPlan(planFormData)
            }

            if (response?.data?.success) {
                message.success(response?.data?.message || `Plan ${isEdit ? 'updated' : 'created'} successfully`)
                setAction(!action)
                setShowEditPlan(false)
            }
        } catch (error) {
            console.log(`Error ${isEdit ? 'updating' : 'creating'} plan`, error.message)
            message.error(error?.response?.data?.message || `Error ${isEdit ? 'updating' : 'creating'} plan`)
        } finally {
            setLoading(false)
        }
    }

    const addFeature = () => {
        setPlanFormData({
            ...planFormData,
            features: [...planFormData.features, '']
        })
    }

    const updateFeature = (index, value) => {
        const updatedFeatures = [...planFormData.features]
        updatedFeatures[index] = value
        setPlanFormData({
            ...planFormData,
            features: updatedFeatures
        })
    }

    const removeFeature = (index) => {
        setPlanFormData({
            ...planFormData,
            features: planFormData.features.filter((_, i) => i !== index)
        })
    }

    return (
        <div className="p-6 mt-16">
            {/* Header Section with Add Button */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Subscription Management</h2>
                    <p className="text-gray-600 mt-1">Manage subscription plans and user subscriptions</p>
                </div>
                <div className="flex gap-3">
                    {activeTab === 'plans' && (
                        <button
                            onClick={() => navigate('/admin/subscriptions/add')}
                            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            <FaPlus className="text-lg" />
                            <span className="font-medium">Create New Plan</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm">Total Plans</p>
                            <p className="text-2xl font-bold">{plans.length}</p>
                        </div>
                        <div className="text-blue-200 text-2xl">
                            <BsCreditCard />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm">Active Plans</p>
                            <p className="text-2xl font-bold">{plans.filter(p => p.isActive).length}</p>
                        </div>
                        <div className="text-green-200 text-2xl">
                            <AiOutlineEye />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100 text-sm">Monthly Plans</p>
                            <p className="text-2xl font-bold">{plans.filter(p => p.billingPeriod === 'month').length}</p>
                        </div>
                        <div className="text-purple-200 text-2xl">
                            <BsCreditCard />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-orange-100 text-sm">Total Subscriptions</p>
                            <p className="text-2xl font-bold">{subscriptions.length}</p>
                        </div>
                        <div className="text-orange-200 text-2xl">
                            <AiFillEdit />
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 mb-6">
                <button
                    onClick={() => setActiveTab('plans')}
                    className={`px-4 py-2 rounded-md font-medium ${
                        activeTab === 'plans'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                    Subscription Plans
                </button>
                <button
                    onClick={() => setActiveTab('subscriptions')}
                    className={`px-4 py-2 rounded-md font-medium ${
                        activeTab === 'subscriptions'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                    User Subscriptions
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder={`Search ${activeTab}...`}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {activeTab === 'plans' ? 'Status' : 'Subscription Status'}
                        </label>
                        {activeTab === 'plans' ? (
                            <Select
                                value={statusFilter}
                                onChange={setStatusFilter}
                                className="w-full"
                                allowClear
                            >
                                <Option value="">All Status</Option>
                                <Option value="active">Active</Option>
                                <Option value="inactive">Inactive</Option>
                            </Select>
                        ) : (
                            <Select
                                value={statusFilter}
                                onChange={setStatusFilter}
                                className="w-full"
                                allowClear
                            >
                                <Option value="">All Status</Option>
                                <Option value="pending">Pending</Option>
                                <Option value="active">Active</Option>
                                <Option value="canceled">Canceled</Option>
                                <Option value="expired">Expired</Option>
                            </Select>
                        )}
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setSearchQuery("")
                                setStatusFilter("")
                            }}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-200 w-full"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced Plans Table */}
            {activeTab === 'plans' && (
                <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plan Details</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Pricing</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {currentItems?.length > 0 ? (
                                    currentItems?.map((plan, index) => (
                                        <tr key={plan._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{plan.displayName}</div>
                                                {plan.badge?.text && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        {plan.badge.text}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {plan.code}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {plan.currency} {plan.price}
                                                </div>
                                                {plan.originalPrice && (
                                                    <div className="text-sm text-gray-500 line-through">
                                                        {plan.currency} {plan.originalPrice}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {plan.durationCount} {plan.durationUnit}(s)
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                    plan.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {plan.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handleEditPlan(plan)}
                                                        className="text-green-600 hover:text-green-900 p-1"
                                                        title="Edit"
                                                    >
                                                        <AiFillEdit className="text-lg" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeletePlan(plan)}
                                                        className="text-red-600 hover:text-red-900 p-1"
                                                        title="Delete"
                                                    >
                                                        <AiFillDelete className="text-lg" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            {loading ? "Loading plans..." : "No subscription plans found"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Subscriptions Table */}
            {activeTab === 'subscriptions' && (
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {currentItems?.length > 0 ? (
                                    currentItems?.map((subscription, index) => (
                                        <tr key={subscription._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {getUserName(subscription.user)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {subscription.plan?.displayName || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                    subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    subscription.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                    subscription.status === 'canceled' ? 'bg-red-100 text-red-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {subscription.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(subscription.startsAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(subscription.endsAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handleViewSubscription(subscription)}
                                                        className="text-blue-600 hover:text-blue-900 p-1"
                                                        title="View Details"
                                                    >
                                                        <AiOutlineEye className="text-lg" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            {loading ? "Loading subscriptions..." : "No subscriptions found"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Pagination */}
            {(activeTab === 'plans' ? filteredPlans : filteredSubscriptions).length > itemsPerPage && (
                <div className="flex justify-center mt-6">
                    <Pagination
                        current={currentPage}
                        total={(activeTab === 'plans' ? filteredPlans : filteredSubscriptions).length}
                        pageSize={itemsPerPage}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        showQuickJumper
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} ${activeTab}`}
                    />
                </div>
            )}

            {/* Edit Plan Modal */}
            {showEditPlan && (
                <Modal
                    title="Edit Subscription Plan"
                    open={showEditPlan}
                    onCancel={() => {
                        setShowEditPlan(false)
                    }}
                    footer={[
                        <button
                            key="cancel"
                            onClick={() => {
                                setShowEditPlan(false)
                            }}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
                        >
                            Cancel
                        </button>,
                        <button
                            key="submit"
                            onClick={() => handlePlanFormSubmit(showEditPlan)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                        >
                            {showEditPlan ? "Update" : "Create"}
                        </button>
                    ]}
                    width={800}
                >
                    <div className="space-y-6 max-h-96 overflow-y-auto">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Code *</label>
                                <input
                                    type="text"
                                    value={planFormData.code}
                                    onChange={(e) => setPlanFormData({...planFormData, code: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., monthly, yearly"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                                <input
                                    type="text"
                                    value={planFormData.displayName}
                                    onChange={(e) => setPlanFormData({...planFormData, displayName: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Monthly Plan"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                <select
                                    value={planFormData.currency}
                                    onChange={(e) => setPlanFormData({...planFormData, currency: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="INR">INR</option>
                                    <option value="USD">USD</option>
                                    <option value="EUR">EUR</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                                <input
                                    type="number"
                                    value={planFormData.price}
                                    onChange={(e) => setPlanFormData({...planFormData, price: parseFloat(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Original Price</label>
                                <input
                                    type="number"
                                    value={planFormData.originalPrice || ''}
                                    onChange={(e) => setPlanFormData({...planFormData, originalPrice: e.target.value ? parseFloat(e.target.value) : null})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Optional"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Period</label>
                                <select
                                    value={planFormData.billingPeriod}
                                    onChange={(e) => setPlanFormData({...planFormData, billingPeriod: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="month">Month</option>
                                    <option value="year">Year</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <input
                                        type="number"
                                        value={planFormData.durationCount}
                                        onChange={(e) => setPlanFormData({...planFormData, durationCount: parseInt(e.target.value)})}
                                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="1"
                                        min="1"
                                    />
                                    <select
                                        value={planFormData.durationUnit}
                                        onChange={(e) => setPlanFormData({...planFormData, durationUnit: e.target.value})}
                                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="day">Day</option>
                                        <option value="week">Week</option>
                                        <option value="month">Month</option>
                                        <option value="year">Year</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
                            {planFormData.features.map((feature, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={feature}
                                        onChange={(e) => updateFeature(index, e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter feature"
                                    />
                                    {planFormData.features.length > 1 && (
                                        <button
                                            onClick={() => removeFeature(index)}
                                            className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                onClick={addFeature}
                                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                            >
                                Add Feature
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">CTA Text</label>
                                <input
                                    type="text"
                                    value={planFormData.ctaText}
                                    onChange={(e) => setPlanFormData({...planFormData, ctaText: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Subscribe Now"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                                <input
                                    type="number"
                                    value={planFormData.sortOrder}
                                    onChange={(e) => setPlanFormData({...planFormData, sortOrder: parseInt(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0"
                                />
                            </div>
                        </div>

                        <div className="flex gap-6">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={planFormData.isActive}
                                    onChange={(e) => setPlanFormData({...planFormData, isActive: e.target.checked})}
                                    className="mr-2"
                                />
                                Active Plan
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={planFormData.canCancelAnytime}
                                    onChange={(e) => setPlanFormData({...planFormData, canCancelAnytime: e.target.checked})}
                                    className="mr-2"
                                />
                                Can Cancel Anytime
                            </label>
                        </div>
                    </div>
                </Modal>
            )}

            {/* View Subscription Modal */}
            {showViewSubscription && (
                <Modal
                    title="Subscription Details"
                    open={showViewSubscription}
                    onCancel={() => setShowViewSubscription(false)}
                    footer={[
                        <button
                            key="close"
                            onClick={() => setShowViewSubscription(false)}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                        >
                            Close
                        </button>
                    ]}
                    width={600}
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">User</p>
                                <p className="font-medium">{getUserName(selectedSubscription.user)}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Plan</p>
                                <p className="font-medium">{selectedSubscription.plan?.displayName}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Status</p>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    selectedSubscription.status === 'active' ? 'bg-green-100 text-green-800' :
                                    selectedSubscription.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    selectedSubscription.status === 'canceled' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                    {selectedSubscription.status}
                                </span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Price</p>
                                <p className="font-medium">{selectedSubscription.plan?.currency} {selectedSubscription.plan?.price}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Start Date</p>
                                <p className="font-medium">{new Date(selectedSubscription.startsAt).toLocaleDateString()}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">End Date</p>
                                <p className="font-medium">{new Date(selectedSubscription.endsAt).toLocaleDateString()}</p>
                            </div>
                        </div>
                        {selectedSubscription.cancelAtPeriodEnd && (
                            <div>
                                <p className="text-sm text-gray-500">Cancel at Period End</p>
                                <p className="font-medium text-orange-600">Yes</p>
                            </div>
                        )}
                        {selectedSubscription.canceledAt && (
                            <div>
                                <p className="text-sm text-gray-500">Canceled At</p>
                                <p className="font-medium">{new Date(selectedSubscription.canceledAt).toLocaleDateString()}</p>
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {loading && <Load />}
        </div>
    )
}

export default AdminSubscriptions
